import { encode, decode } from 'js-base64';
import { formationPitchPositions } from './config/pitchPositions';

import { TeamName, TeamResult, Player, Team, Formation, PickTeamResult } from './types';

const teamNames = ['A', 'B', 'C', 'D'];

export const defaultFormation = (team: Team) => {
  if (team.players.length < 7) {
    return null;
  }
  if (team.players.length === 7) {
    return Formation.ThreeTwoOne;
  }
  if (team.players.length === 8) {
    return Formation.ThreeThreeOne;
  }
  if (team.players.length === 9) {
    return Formation.ThreeThreeTwo;
  }
  return Formation.ThreeThreeOne;
};

export const getAvailableFormations = (team: Team) => {
  if (team.players.length < 7) {
    return {};
  }
  let formations: Record<number, Formation[]> = {};
  if (team.players.length >= 7) {
    formations[7] = [
      Formation.ThreeTwoOne,
      Formation.TwoThreeOne,
      Formation.TwoTwoTwo,
    ];
  }
  if (team.players.length >= 8) {
    formations[8] = [
      Formation.ThreeThreeOne,
      Formation.ThreeTwoTwo,
      Formation.TwoThreeTwo,
      Formation.TwoFourOne,
    ];
  }
  if (team.players.length >= 9) {
    formations[9] = [
      Formation.ThreeThreeTwo,
      Formation.ThreeTwoThree,
      Formation.ThreeFourOne,
    ];
  }
  return formations;
};

export const getFormationPositions = (formation?: Formation) => {
  const formationConfig = formationPitchPositions.find(
    ({ formation: f }) => f === formation
  );
  if (!formationConfig) {
    return null;
  }
  return formationConfig.positions;
};

function shuffle(players: Player[]) {
  for (let i = players.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * i);
    const temp = players[i];
    players[i] = players[j];
    players[j] = temp;
  }
}

export function draw(players: Player[], numTeam: number, date: string, doNotShuffle?: boolean) {
  if (!doNotShuffle) {
    shuffle(players);
  }

  // Split the players equally to every team
  const teamSize = Math.floor(players.length / numTeam);
  const result: TeamResult = {
    date,
    teams: [],
  };
  for (let i = 0; i < numTeam; i++) {
    let team = result.teams.find((team) => team.name === teamNames[i]);

    if (!team) {
      team = {
        name: teamNames[i] as TeamName,
        players: [],
      };
      result.teams.push(team);
    }
    team.players = players.slice(i * teamSize, (i + 1) * teamSize);
  }

  // Split the remaining
  const remaining = players.slice(numTeam * teamSize, players.length);
  for (let i = 0; i < remaining.length; i++) {
    let team = result.teams.find((team) => team.name === teamNames[i]);
    if (team) {
      team.players.push(remaining[i]);
    }
  }
  return result;
}

export function fillTeams(
  teams: Team[],
  players: Player[],
  teamSize: number,
  leftOver: number = 0
) {
  let i = 0;
  let j = 0;

  while (i < players.length) {
    // eslint-disable-next-line no-loop-func
    let team = teams.find((team) => team.name === teamNames[j]);

    if (!team) {
      team = {
        name: teamNames[j] as TeamName,
        players: [],
      };
      teams.push(team);
    }

    // Try to fill the teams with as many core players as possible
    if (team.players.length === (j + 1 <= leftOver ? teamSize + 1 : teamSize)) {
      j++;
      if (j >= teamNames.length) {
        break;
      }
      continue;
    }
    team.players.push(players[i]);
    i++;
  }
  return players.slice(i);
}

export function drawWithCoreTeam(
  players: Player[],
  numTeam: number,
  date: string
) {
  shuffle(players);

  let teamSize = Math.floor(players.length / numTeam);
  if (teamSize === 0) {
    teamSize = players.length;
  }

  // There are extra players to be filled after teams are equally split
  const leftOver = players.length - teamSize * numTeam;
  const result: TeamResult = {
    date,
    teams: [],
  };

  fillTeams(result.teams, players, teamSize, leftOver);

  return result;
}

export function objectToBase64(obj: any) {
  return encode(JSON.stringify(obj)).replaceAll('/', '__');
}

export function base64ToObject<T>(base64?: string) {
  if (!base64) return null;
  const validBase64 = base64.replaceAll('__', '/');
  return JSON.parse(decode(validBase64)) as T;
}

const isValidTeamFormation = (team: Team) => {
  if (!team.formation) {
    return false;
  }
  const totalPlayers = team.formation
    .split('-')
    .reduce((acc, num) => acc + parseInt(num), 0);
  return totalPlayers <= team.players.length - 1;
};

export function drawFormations(result: TeamResult) {
  result?.teams.forEach((team) => {
    const hasFormationSet = !!team.formation;

    if (!isValidTeamFormation(team)) {
      const formation = defaultFormation(team);
      if (formation) {
        team.formation = formation;
      }
    }

    if (hasFormationSet) {
      return;
    }

    const formationConfig = formationPitchPositions.find(
      ({ formation: f }) => f === team.formation
    );
    if (formationConfig) {
      const pitchPositions = formationConfig.positions.map(
        ({ position }) => position
      );
      for (let i = 0; i < team.players.length; i++) {
        if (!team.players[i].position) {
          team.players[i].position = pitchPositions[i];
        } else {
          if (!pitchPositions.includes(team.players[i].position!)) {
            delete team.players[i].position;
          }
        }
      }
    }
  });
}

export const pickRandomCaptain = (pickResult: PickTeamResult) => {
  const allCaptains = pickResult.teams.map((team) => team.captain);
  const pickedCaptains = pickResult.pickedCaptains;
  const availableCaptains = allCaptains.filter(
    (captain) => !pickedCaptains.includes(captain)
  );

  // Exclude the last cap of the previous round so that he won't be last again
  // when there are only 2 caps available. If he's not in the list, let it randomize
  if (
    pickResult.pickRound > 1 &&
    availableCaptains.length === 2 &&
    pickResult.prevLastCaptain
  ) {
    const cap = availableCaptains.find(
      (captain) => captain === pickResult.prevLastCaptain
    );
    if (cap) {
      return cap;
    }
  }

  // Exclude first cap of the previous round so that he won't be first again
  // This is only for when the round starts
  if (
    pickResult.pickRound > 1 &&
    !pickedCaptains.length &&
    pickResult.prevFirstCaptain
  ) {
    const previousCaptainIndex = availableCaptains.indexOf(
      pickResult.prevFirstCaptain
    );
    if (previousCaptainIndex >= 0) {
      availableCaptains.splice(previousCaptainIndex, 1);
    }
  }

  if (availableCaptains.length === 1) {
    return availableCaptains[0];
  }

  // Randomize the available captains and choose one
  const randomIndex = Math.floor(Math.random() * availableCaptains.length);
  return availableCaptains[randomIndex];
};
